// Native status card, shared by cached instances and raw URL mounts.
import type { EditCtx, StatusEntry, StatusTemplateArr } from './types';
import { pick, str, num } from './read';
import { iconSpan, clickableRef } from './dom';
import { renderStatusModifyForm } from './render-status-form';
import { durChip, fmtCampaign, fillStatusList } from './status-row';
import { statusRunes } from '../shared/status-rune';
import { parseText } from '../shared/parse-text';
import { picturesOf, itemVideos } from '../shared/array-types';
import { buildCarousel } from '../shared/build-carousel';
import { sourceFooterHtml } from '../object-card/card-shell';

// (The GM Edit/Delete row + the bordered time blocks were removed — the read panel now MERGES the full
//  modify form inline below the info when editable, and the start/end use the shared badged durChip.)

function buildTitle(effectName: string, effectType: string, templateId: string, statusId: string, useRune: boolean): HTMLElement {
    const title = document.createElement('div');
    title.className = 'card-title';
    const row = document.createElement('div');
    row.className = 'd-flex';

    const nameCol = document.createElement('div');
    nameCol.className = 'flex-grow-1';
    const nameH = document.createElement('h5');
    nameH.textContent = effectName || 'Status Effect';
    nameCol.appendChild(nameH);

    const iconsCol = document.createElement('div');
    const iconsH = document.createElement('h5');
    const link = (id: string, glyph: string): HTMLElement => {
        const a = document.createElement('a');
        a.href = `${BASE_URL}item.php?id=${encodeURIComponent(id)}`;
        a.appendChild(iconSpan(glyph));
        return a;
    };
    // LEFT icon = the effect's OWN icon (its registered PNG, keyed by the template id — e.g. fully_rested),
    // standard statuses only. An ability/rune status already shows its rune medallion above (and has no
    // registered PNG), so skip the left glyph there. RIGHT icon = the item-type marker (status_effect),
    // which just signals "this is a status-effect item". (owner 2026-06-26: the two were duplicate type icons.)
    if (templateId && !useRune) { iconsH.appendChild(link(templateId, templateId)); iconsH.appendChild(document.createTextNode(' ')); }
    if (statusId) iconsH.appendChild(link(statusId, effectType || 'status_effect'));
    iconsCol.appendChild(iconsH);

    row.appendChild(nameCol);
    row.appendChild(iconsCol);
    title.appendChild(row);
    return title;
}

/** AOE geometry list (only when status_aoe_type is a real uuid). */
function buildAoe(entry: StatusEntry): HTMLElement | null {
    const aoeType = str(pick(entry, 'status_aoe_type'));
    const isUuid = typeof checkIfValidUUID === 'function' ? checkIfValidUUID(aoeType) : !!aoeType;
    if (!isUuid) return null;

    const wrap = document.createElement('div');
    wrap.className = 'mb-3';
    const ul = document.createElement('ul');
    ul.className = 'list-group';
    const li = (): HTMLElement => { const e = document.createElement('li'); e.className = 'list-group-item'; return e; };

    const typeLi = li();
    typeLi.appendChild(document.createTextNode('AOE Type: '));
    const typeIcon = iconSpan(aoeType);
    typeIcon.setAttribute('role', 'button');
    typeIcon.addEventListener('click', () => { if (typeof openItemInfo === 'function') openItemInfo(aoeType); });
    typeLi.appendChild(typeIcon);
    ul.appendChild(typeLi);

    const metric = (glyph: string, label: string, value: string): HTMLElement => {
        const e = li();
        e.appendChild(iconSpan(glyph));
        e.appendChild(document.createTextNode(` ${label}: ${value}`));
        return e;
    };
    const distVal = str(pick(entry, 'status_aoe_distance'));
    if (distVal !== '') ul.appendChild(metric('distance', 'AOE Distance', distVal));

    // Depth / Height only when meaningful (a flat / aura zone has neither — don't show empty rows).
    if (num(pick(entry, 'status_aoe_distance_depth')) !== 0) {
        ul.appendChild(metric('depth', 'AOE Distance Depth', str(pick(entry, 'status_aoe_distance_depth'))));
    }
    if (num(pick(entry, 'status_aoe_distance_height')) !== 0) {
        ul.appendChild(metric('height', 'AOE Distance Height', str(pick(entry, 'status_aoe_distance_height'))));
    }

    // Origin / Distance points only when placed (a follows-target aura has none — it lives on the target).
    const ox = str(pick(entry, 'status_origin_point_x'));
    const oz = str(pick(entry, 'status_origin_point_z'));
    if (ox !== '' || oz !== '') {
        const originPt = li();
        originPt.textContent = `Origin Point: ${ox}, ${oz}`;
        ul.appendChild(originPt);
    }

    const dpx = str(pick(entry, 'status_distance_point_x'));
    const dpz = str(pick(entry, 'status_distance_point_z'));
    if (dpx !== '' || dpz !== '') {
        const distPt = li();
        distPt.textContent = `Distance Point: ${dpx}, ${dpz}`;
        ul.appendChild(distPt);
    }

    wrap.appendChild(ul);
    return wrap;
}

/**
 * CARD-size rune medallion mount (LOD-full, size 96) — the same shape the object-card header uses
 * (`runeIconMountHtml(arr, 96)`), NOT the tiny inline `ft-rune-status` chip. Paired with the `ft-rune-card`
 * scope class on the card so css/style.css draws the bordered header box + positions the medallion.
 */
function runeCardMountHtml(def: StatusTemplateArr, size = 96): string {
    const payload: { items: unknown; item_layer_types: unknown; catalog?: unknown } = {
        items: def?.items ?? {},
        item_layer_types: def?.item_layer_types ?? {},
    };
    const cat = def?.catalog;
    if (cat && typeof cat === 'object' && Object.keys(cat as object).length) payload.catalog = cat;
    let json = 'null';
    try { json = JSON.stringify(payload); } catch { json = 'null'; }
    json = json.replace(/<\//g, '<\\/');
    return `<div class="ft-rune-mount" data-rune-size="${size}"><script type="application/json" class="ft-rune-blob">${json}</script></div>`;
}

/**
 * Paint the status-effect card into `container` from a full-nested casterCache status entry. Returns
 * true on success; false leaves the mount un-done for a later re-scan.
 */
export async function buildStatusEffectCard(entry: StatusEntry, container: HTMLElement): Promise<boolean> {
    try {
        const def: StatusTemplateArr = typeof entry.status_arr === 'object' ? entry.status_arr : {};
        const tmpl = (def.items ?? {}) as Record<string, unknown>;
        const effectName = str(tmpl.item_name) || 'Status Effect';
        const effectType = str(tmpl.item_type);
        const templateId = str(tmpl.item_id);
        const statusId = str(pick(entry, 'status_id'));
        const casterId = str(pick(entry, 'status_caster_id'));
        const targetId = str(pick(entry, 'status_target_id'));
        const originId = str(pick(entry, 'status_origin_id'));

        // A "standard" status (Prone, Encumbered, …) has a bespoke PNG registered by its template UUID. A
        // status applied FROM an ability/spell/curse (e.g. Darkness Sphere) has NO registered icon, so it
        // renders the CARD-size rune medallion (ft-rune-card scope + size-96 mount), exactly like the
        // object-card header — not iconMap's "?" and not the tiny inline sigil.
        const useRune = !!(templateId && statusRunes(templateId, def));

        const cardClass = container.getAttribute('data-ft-card-class') || 'col-md-12';
        const card = document.createElement('div');
        card.className = `card ${cardClass}${useRune ? ' ft-rune-card' : ''}`.trim();

        const body = document.createElement('div');
        body.className = 'card-body text-left';
        // Ability/rune statuses keep their CARD-size rune medallion at the top; standard statuses no longer
        // get a big header PNG — the title's own-icon is enough (owner direction 2026-06-26).
        if (useRune) {
            const mountWrap = document.createElement('div');
            mountWrap.innerHTML = runeCardMountHtml(def, 96);
            const mountEl = mountWrap.firstElementChild;
            if (mountEl) body.appendChild(mountEl);
        }
        const media = buildCarousel({ domId: `status_${statusId}`, itemName: effectName,
            pics: picturesOf(def), videos: itemVideos(def), height: 355 });
        if (media) {
            const mediaWrap = document.createElement('div');
            mediaWrap.innerHTML = media;
            body.appendChild(mediaWrap);
        }
        body.appendChild(buildTitle(effectName, effectType, templateId, statusId, useRune));

        // INSTANCE INFO FIRST (consistent top placement, owner 2026-06-26): caster / target / origin + the
        // badged start/end duration chips. Then an <hr>, then the description logic below.
        const info = document.createElement('div');
        info.className = 'mb-2';
        // Light, border-less rows — the old per-row section() boxes nested inside this (also-bordered) box
        // were the "border hell" (owner 2026-06-26). One subtle container, flat rows inside.
        const infoRow = (): HTMLElement => {
            const d = document.createElement('div');
            d.className = 'd-flex align-items-center flex-wrap gap-2 py-1';
            return d;
        };

        if (casterId) {
            const block = infoRow();
            const badge = document.createElement('span'); badge.className = 'badge bg-primary'; badge.appendChild(iconSpan('caster'));
            block.appendChild(badge); block.appendChild(document.createTextNode(' '));
            block.appendChild(clickableRef(casterId, str(pick(entry, 'status_caster_name')), str(pick(entry, 'status_caster_type'))));
            info.appendChild(block);
        }
        if (targetId) {
            const block = infoRow();
            const badge = document.createElement('span'); badge.className = 'badge bg-danger'; badge.appendChild(iconSpan('target'));
            block.appendChild(badge); block.appendChild(document.createTextNode(' '));
            block.appendChild(clickableRef(targetId, str(pick(entry, 'status_target_name')), str(pick(entry, 'status_target_type'))));
            info.appendChild(block);
        }
        if (originId) {
            const block = infoRow();
            const inner = document.createElement('div');
            inner.appendChild(iconSpan('status_effect_origin'));
            inner.appendChild(document.createTextNode(': '));
            inner.appendChild(clickableRef(originId, str(pick(entry, 'status_origin_id_item_name')) || str(pick(entry, 'status_origin_name')) || effectName, str(pick(entry, 'status_origin_id_item_type'))));
            block.appendChild(inner);
            info.appendChild(block);
        }

        const aoe = buildAoe(entry);
        if (aoe) info.appendChild(aoe);

        // Badged start/end chips — the SAME durChip the Modify Status Effects list uses (owner wanted parity).
        // fmtCampaign returns null for an all-zero tuple, so a permanent end shows the permanent glyph.
        const startStamp = fmtCampaign(entry, 'starts');
        const endStamp = fmtCampaign(entry, 'ends');
        const durRow = document.createElement('div');
        durRow.className = 'd-flex flex-wrap align-items-center gap-2 py-1';
        if (startStamp) durRow.appendChild(durChip('start', startStamp));
        durRow.appendChild(durChip('end', endStamp));
        info.appendChild(durRow);

        body.appendChild(info);

        // THEN <hr> + the description logic: the normal item_description (so we know WHAT it is) + the FULL
        // catalog info — the status is full-nested, so window.renderItemListDisplay paints the template's
        // properties / effect layers / abilities with NO server lookup, like openItemInfo on a normal item.
        body.appendChild(document.createElement('hr'));
        const desc = str(tmpl.item_description);
        if (desc) {
            // item_description is rich HTML with FateTide [label](type:id) link markup — parse it (clickable
            // refs + icons), NOT textContent (which would dump the raw markup).
            const d = document.createElement('div');
            d.className = 'mb-3';
            d.innerHTML = parseText(desc);
            body.appendChild(d);
        }
        const ld = document.createElement('div');
        body.appendChild(ld);
        try {
            // names={} → renderItemListDisplay fills name gaps from the arr's own .catalog (mergeNamesWithCatalog).
            window.renderItemListDisplay?.(def, {}, ld);
        } catch (e) { /* list-display global not ready — non-fatal */ }

        const children = (entry.statuses as { target?: StatusEntry[] } | undefined)?.target;
        if (children?.length) {
            const nested = document.createElement('div');
            nested.setAttribute('data-ft-nested-statuses', statusId);
            fillStatusList(nested, children, { statusId, campaignId: '', encounterId: '', casterId: '' }, { canEdit: false, canCampaign: false });
            body.appendChild(nested);
        }

        // MERGE the edit LOGIC inline below the read info (the status is full-nested + status_id is referenced,
        // so no AJAX needed) — only when editable. Compact = the modify form skips its own title + caster/target,
        // since the panel above already shows them; it contributes the action buttons + editable times + save.
        if (container.getAttribute('data-ft-can-edit') === '1') {
            const ctx: EditCtx = {
                statusId,
                campaignId: container.getAttribute('data-ft-campaign-id') || '',
                encounterId: container.getAttribute('data-ft-encounter-id') || '',
                casterId: container.getAttribute('data-ft-viewer-caster-id') || '',
            };
            const formWrap = document.createElement('div');
            formWrap.className = 'mt-3 pt-3 border-top';
            renderStatusModifyForm(entry, ctx, formWrap, { compact: true });
            body.appendChild(formWrap);
        }

        card.appendChild(body);

        container.innerHTML = '';
        const footer = document.createElement('div');
        footer.innerHTML = sourceFooterHtml(def);
        card.appendChild(footer);
        container.appendChild(card);
        // Hydrate everything the title medallion AND the embedded list-display painted: rune sigils, plain
        // icon placeholders, and any nested list-display mounts. No-ops when nothing of that kind is present.
        try { (window as Window & { ftMountCarousels?: (root: Element) => void }).ftMountCarousels?.(container); } catch { /* footer pass covers init */ }
        try { window.ftMountRuneIcons?.(container); } catch { /* footer pass covers init */ }
        try { window.ftMountIcons?.(container); } catch { /* non-fatal */ }
        try { window.ftMountListDisplays?.(container); } catch { /* non-fatal */ }
        try { window.ftMountParsedText?.(container); } catch { /* non-fatal */ }
        return true;
    } catch (e) {
        console.error('buildStatusEffectCard failed', e);
        return false;
    }
}
